import { gql } from '@apollo/client';
import type { ScanResult } from '@/lib/scanning/scanResult';
import { productCodeFromScan } from '@/pages/ScannedProduct/productStock';

/** Home-screen lookup for the RMA # barcode on a return's RMA document. */
export const WAREHOUSE_RETURN_BY_NUMBER = gql`
  query warehouseReturnByNumber($vendorId: ID!, $clientReturnNumber: String!) {
    vendorReturnByNumber(vendorId: $vendorId, clientReturnNumber: $clientReturnNumber) {
      id
      clientReturnNumber
      status
    }
  }
`;

/** Where a scanned RMA opens: its receive screen (which explains any status it can't take scans in). */
export const returnScreenFor = (clientReturn: { id?: string | null } | null | undefined) =>
  clientReturn?.id ? `/return-receive/${clientReturn.id}` : null;

/** An RMA still awaiting its product — what scanning its document is for. */
export const returnAwaitsProduct = (clientReturn: { status?: string | null } | null | undefined) =>
  clientReturn?.status === 'rma_issued';

/** A receiving scan as `clientReturnReceiveScan` takes it (ClientReturnReceiveScanInput). */
export type RmaReceiveScanInput = {
  itemId?: string | null;
  gtin?: string | null;
  code?: string | null;
  serialNumber?: string | null;
  lot?: string | null;
  expiration?: string | null;
  quantity?: number | null;
};

type RmaScanLike = Pick<ScanResult, 'gtin' | 'serialNumber' | 'lot' | 'expiration' | 'kind' | 'text'> &
  Partial<Pick<ScanResult, 'sscc'>>;

const RMA_NUMBER = /^R\d{1,8}$/i;
const AIM_IDENTIFIER = /^\][A-Za-z][0-9]/;

/**
 * The RMA # a scan carries — the Code 128 on an RMA document (`R00000123`), or the number typed
 * with its zeros left off (`R123`) — else null. A wedge's AIM prefix is dropped.
 */
export const rmaNumberFromScan = (result: Pick<ScanResult, 'serialNumber' | 'kind' | 'text'>) => {
  if (result.serialNumber || result.kind !== 'raw') return null;
  let text = (result.text || '').trim();
  if (AIM_IDENTIFIER.test(text)) text = text.slice(3).trim();
  return RMA_NUMBER.test(text) ? text.toUpperCase() : null;
};

/**
 * What a warehouse scan says, for an RMA receiving scan. A retail barcode (UPC / EAN) is recognised
 * from its digits first — some parse as a GS1 element string with a bogus serial — and sent as the
 * product code; a 2D unit barcode or a case label is sent with its GTIN, serial, lot and
 * expiration; an SSCC as its serial; anything else as typed text, matched on the server as an NDC
 * or product code. Null when there is nothing to send.
 */
export function rmaReceiveScanFromResult(result: RmaScanLike): RmaReceiveScanInput | null {
  const productCode = productCodeFromScan(result as ScanResult);
  if (productCode) return { code: productCode };
  if (result.serialNumber || result.gtin) {
    return {
      gtin: result.gtin,
      serialNumber: result.serialNumber,
      lot: result.lot,
      expiration: result.expiration
    };
  }
  if (result.sscc) return { serialNumber: result.sscc };
  const text = (result.text || '').trim();
  return text ? { code: text } : null;
}
